import Swal from "sweetalert2";
import Cropper from "cropperjs";
import { PerfilService } from "../services/PerfilService.js";
import { ErrorNegocio } from "../services/VacacionesService.js";
import { Loading } from "./Loading.js";

// Foto de perfil: el avatar del header abre un modal para tomar/elegir una foto
// (camara o galeria en el celular, examinar en la PC), recortarla cuadrada con
// zoom y arrastre (Cropper.js, tactil) y subirla ya reducida a 700x700 JPEG (la foto
// tambien la usa el sistema de presentismo, por eso no va mas chica).
const LADO = 700;
const CALIDAD_JPEG = 0.85;

export const FotoPerfil = {
  /** Markup del avatar (foto o icono). `clase` extra para el tamanio. */
  avatarHtml(clase = "") {
    const url = PerfilService.urlFoto();
    return url
      ? `<img src="${url}" alt="Tu foto" class="avatar ${clase}" />`
      : `<span class="avatar avatar-vacio ${clase}"><i class="fas fa-user"></i></span>`;
  },

  /** Refresca todos los avatares de la pantalla (tras subir una foto). */
  refrescarAvatares() {
    document.querySelectorAll("[data-avatar]").forEach((el) => {
      el.innerHTML = FotoPerfil.avatarHtml(el.dataset.avatar);
    });
  },

  async abrir() {
    const url = PerfilService.urlFoto();
    // Dos botones propios apilados (los de SweetAlert2 van en fila y en celular se
    // desacomodan). La camara en vivo hay que pedirla explicitamente (capture), y con
    // capture el celular ya no ofrece la galeria; por eso van separados.
    let origen = null;
    await Swal.fire({
      title: "Tu foto",
      html:
        '<div class="text-center">' +
        (url
          ? `<img src="${url}" alt="Tu foto" class="avatar avatar-xl mb-3" />`
          : '<span class="avatar avatar-vacio avatar-xl mb-3"><i class="fas fa-user"></i></span>') +
        '<p class="text-muted small mb-3">Se ve en el encabezado de la app. Podés cambiarla cuando quieras.</p>' +
        '<div class="d-grid gap-2">' +
        '<button type="button" class="btn btn-primary btn-lg" data-origen="camara"><i class="fas fa-camera me-2"></i>Sacar una foto</button>' +
        '<button type="button" class="btn btn-outline-primary btn-lg" data-origen="galeria"><i class="fas fa-images me-2"></i>Elegir de la galería</button>' +
        "</div></div>",
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Cerrar",
      didOpen: (modal) => {
        modal.querySelectorAll("[data-origen]").forEach((btn) => {
          btn.addEventListener("click", () => {
            origen = btn.dataset.origen;
            Swal.close();
          });
        });
      },
    });
    if (!origen) return;

    const archivo = await FotoPerfil.elegirArchivo(origen);
    if (!archivo) return;

    const dataUrl = await FotoPerfil.recortar(archivo);
    if (!dataUrl) return;

    Loading.mostrar();
    try {
      await PerfilService.subirFoto(dataUrl);
      FotoPerfil.refrescarAvatares();
      Swal.fire({ toast: true, position: "top-end", timer: 2500, showConfirmButton: false, icon: "success", title: "Foto actualizada" });
    } catch (error) {
      console.error("Error subiendo la foto:", error);
      const texto = error instanceof ErrorNegocio ? error.message : "No pudimos subir la foto. Intentá de nuevo más tarde.";
      await Swal.fire({ icon: "error", title: "No se pudo guardar", text: texto });
    } finally {
      Loading.ocultar();
    }
  },

  // <input type="file"> oculto y reutilizable. "camara": capture="user" abre la camara
  // frontal en iPhone/Android (en PC, examinar). "galeria": sin capture, abre fotos/archivos.
  // Si el usuario cancela el dialogo no hay evento confiable en todos los navegadores:
  // la promesa queda sin resolver y no pasa nada (no hay estado pendiente).
  elegirArchivo(origen = "galeria") {
    return new Promise((resolve) => {
      let input = document.getElementById("inputFotoPerfil");
      if (!input) {
        input = document.createElement("input");
        input.id = "inputFotoPerfil";
        input.type = "file";
        input.accept = "image/*";
        input.hidden = true;
        document.body.appendChild(input);
      }
      if (origen === "camara") input.setAttribute("capture", "user");
      else input.removeAttribute("capture");
      input.value = "";
      input.onchange = () => resolve(input.files?.[0] || null);
      input.click();
    });
  },

  // Modal con Cropper.js. Devuelve la foto recortada como data URL JPEG, o null si cancela.
  async recortar(archivo) {
    if (!archivo.type.startsWith("image/")) {
      await Swal.fire({ icon: "warning", title: "Elegí una imagen", text: "El archivo no es una foto." });
      return null;
    }

    const objectUrl = URL.createObjectURL(archivo);
    let cropper = null;

    const resultado = await Swal.fire({
      title: "Recortá tu foto",
      html:
        '<div class="cropper-caja"><img id="imgRecorte" src="' + objectUrl + '" alt="" /></div>' +
        '<p class="text-muted small mt-2 mb-0">Arrastrá para mover y pellizcá o usá la rueda para acercar.</p>',
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-check me-1"></i>Usar esta foto',
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
      didOpen: () => {
        cropper = new Cropper(document.getElementById("imgRecorte"), {
          aspectRatio: 1,
          viewMode: 1,
          dragMode: "move",
          autoCropArea: 0.9,
          guides: false,
          center: false,
          highlight: false,
          background: false,
          cropBoxMovable: false,
          cropBoxResizable: false,
          toggleDragModeOnDblclick: false,
          responsive: true,
          checkOrientation: true, // fotos de celular con EXIF rotado
        });
      },
      willClose: () => {
        cropper?.destroy();
        URL.revokeObjectURL(objectUrl);
      },
      preConfirm: () => {
        const canvas = cropper.getCroppedCanvas({
          width: LADO,
          height: LADO,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: "high",
          fillColor: "#fff",
        });
        return canvas.toDataURL("image/jpeg", CALIDAD_JPEG);
      },
    });

    return resultado.isConfirmed ? resultado.value : null;
  },
};
